import type { ImportPositionCandidateDto } from "@partikelart/environment/dtos";

/**
 * Ranks existing trap positions against the sampling site / trap number
 * parsed from an imported lab report.
 *
 * Pure scoring logic (no Nest dependencies):
 * - exact normalized name match → 1.0
 * - one name contains the other → 0.7
 * - token overlap (Jaccard) → up to 0.6
 * - trap number equals the numeric suffix of the position name → +0.3 bonus
 * Scores are capped at 1.0; candidates below {@link MIN_SCORE} are dropped.
 */

export interface MatchablePosition {
	id: string;
	name: string | null;
	areaId: string;
	areaName: string;
}

const MIN_SCORE = 0.3;
const BEST_MATCH_THRESHOLD = 0.8;
const MAX_CANDIDATES = 5;

/** Lowercase, strip diacritics, collapse non-alphanumerics to single spaces. */
export function normalizeName(raw: string): string {
	return raw
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-z0-9]+/g, " ")
		.trim();
}

function tokenize(normalized: string): Set<string> {
	return new Set(normalized.split(" ").filter((t) => t.length > 0));
}

function jaccard(a: Set<string>, b: Set<string>): number {
	if (a.size === 0 || b.size === 0) return 0;
	let intersection = 0;
	for (const t of a) {
		if (b.has(t)) intersection++;
	}
	return intersection / (a.size + b.size - intersection);
}

/** Extract a trailing number from a position name ("Trap 1", "PF-01" → "1"). */
function numericSuffix(name: string): string | null {
	const m = name.match(/(\d+)\s*$/);
	return m ? String(Number(m[1])) : null;
}

/** Score a single position against the parsed sampling site / trap no. */
export function scorePosition(samplingSite: string | null, trapNo: string | null, positionName: string | null): number {
	let score = 0;
	if (samplingSite && positionName) {
		const site = normalizeName(samplingSite);
		const name = normalizeName(positionName);
		if (site.length > 0 && name.length > 0) {
			if (site === name) {
				score = 1;
			} else if (site.includes(name) || name.includes(site)) {
				score = 0.7;
			} else {
				score = 0.6 * jaccard(tokenize(site), tokenize(name));
			}
		}
	}
	if (trapNo && positionName && numericSuffix(positionName) === String(Number(trapNo) || trapNo)) {
		score += 0.3;
	}
	return Math.min(score, 1);
}

export interface RankedCandidates {
	candidates: ImportPositionCandidateDto[];
	bestMatchPositionId: string | null;
}

/**
 * Rank all positions of a site against the parsed report fields and return
 * the top candidates plus a best match when confidence is high enough.
 */
export function rankPositionCandidates(
	samplingSite: string | null,
	trapNo: string | null,
	positions: MatchablePosition[],
): RankedCandidates {
	const candidates = positions
		.map((p) => ({
			positionId: p.id,
			name: p.name ?? "",
			areaId: p.areaId,
			areaName: p.areaName,
			score: Math.round(scorePosition(samplingSite, trapNo, p.name) * 100) / 100,
		}))
		.filter((c) => c.score >= MIN_SCORE)
		.sort((a, b) => b.score - a.score)
		.slice(0, MAX_CANDIDATES);

	const best = candidates[0];
	return {
		candidates,
		bestMatchPositionId: best && best.score >= BEST_MATCH_THRESHOLD ? best.positionId : null,
	};
}
